import { JobType, WhoType } from '#shared/types/Job'
import { OrgType } from '#shared/types/Org'
import { SkillType } from '#shared/types/Skill'
import type { ListingSeoLink } from '@/components/common/ListingSeoLinks.vue'
import { slovakRegions } from '@/constants/slovakRegions'

/*
 * Internal links shown under the results of the listing pages (ListingSeoLinks.vue).
 * Filtered views point at query URLs of the same listing; those canonicalize to the
 * listing itself (app.vue), so they help readers without creating duplicate pages.
 */

const nonProfitJobsLink: ListingSeoLink = {
    to: { name: 'praca', query: { orgTypes: OrgType.nonProfit } },
    label: 'Práca v neziskovom sektore',
    description: 'Platené pracovné ponuky a stáže v neziskových organizáciách.',
    section: 'jobs'
}

const becomeVolunteerLink: ListingSeoLink = {
    to: { name: 'chcem-byt-dobrovolnik' },
    label: 'Ako sa zapojiť ako dobrovoľník',
    description: 'Ako začať, vybrať si projekt a prihlásiť sa cez GROWNi.',
    section: 'volunteering'
}

const findVolunteersLink: ListingSeoLink = {
    to: { name: 'ako-najst-dobrovolnika' },
    label: 'Ako nájsť dobrovoľníkov',
    description: 'Pre organizácie: ako zverejniť príležitosť a získať dobrovoľníkov.',
    section: 'organizations'
}

const mentorsLink: ListingSeoLink = {
    to: { name: 'mentori' },
    label: 'Mentoring',
    description: 'Poraď sa s mentorom z praxe o kariére, štúdiu či vlastnom projekte.',
    section: 'mentoring'
}

const selfKnowledgeLink: ListingSeoLink = {
    to: { name: 'sebapoznanie' },
    label: 'Sebapoznanie',
    description: 'Otestuj si osobnosť, kariérne záujmy, hodnoty a silné stránky.',
    section: 'selfKnowledge'
}

export const jobNicheLinks: ListingSeoLink[] = [
    {
        ...nonProfitJobsLink,
        description: 'Ponuky od neziskových organizácií, nadácií a občianskych združení.'
    },
    {
        to: '/staze',
        label: 'Stáže pre študentov',
        description: 'Študentské stáže, na ktorých získaš prvú prax ešte počas školy.',
        section: 'jobs'
    },
    {
        to: { name: 'praca', query: { who: WhoType.cerstvyAbsolventVs } },
        label: 'Práca pre absolventov',
        description: 'Ponuky vhodné pre čerstvých absolventov vysokých škôl.',
        section: 'jobs'
    },
    {
        to: { name: 'praca', query: { jobTypes: JobType.partTime } },
        label: 'Práca na skrátený úväzok',
        description: 'Kratšie úväzky, ktoré zvládneš popri škole, rodine či inej práci.',
        section: 'jobs'
    },
    {
        to: { name: 'verejny-sektor-praca-a-staze' },
        label: 'Práca vo verejnom sektore',
        description: 'Práca a stáže v štátnej správe, samospráve a verejných inštitúciách.',
        section: 'jobs'
    },
    {
        to: '/flexibilna-praca',
        label: 'Flexibilná práca',
        description: 'Ponuky s pružným pracovným časom alebo prácou z domu.',
        section: 'jobs'
    }
]

export const jobCityLinks: ListingSeoLink[] = [
    'Bratislava',
    'Košice',
    'Žilina',
    'Banská Bystrica',
    'Nitra',
    'Prešov',
    'Trenčín'
].map((city) => ({
    to: { name: 'praca', query: { jobLocations: city } },
    label: city,
    icon: 'location-outline'
}))

export const jobRelatedLinks: ListingSeoLink[] = [
    mentorsLink,
    {
        to: { name: 'dobrovolnictvo-dobrovolnicke-projekty' },
        label: 'Dobrovoľníctvo',
        description: 'Získaj skúsenosti a kontakty v dobrovoľníckych projektoch.',
        section: 'volunteering'
    },
    selfKnowledgeLink,
    {
        to: '/diskusie/praca',
        label: 'Diskusie o práci a stážach',
        description: 'Pýtaj sa komunity na pohovory, stáže či prvé kroky v kariére.',
        section: 'discussions'
    }
]

export const volunteeringRegionLinks: ListingSeoLink[] = [
    ...slovakRegions.map((region) => ({
        to: { name: 'dobrovolnictvo-dobrovolnicke-projekty', query: { locations: region } },
        label: `${region} kraj`,
        icon: 'location-outline'
    })),
    {
        to: { name: 'dobrovolnictvo-dobrovolnicke-projekty', query: { remote: 'true' } },
        label: 'Online, na diaľku',
        icon: 'globe'
    }
]

const volunteeringDiscussionsLink: ListingSeoLink = {
    to: '/diskusie/dobrovolnictvo',
    label: 'Diskusie o dobrovoľníctve',
    description: 'Pýtaj sa a raď sa s dobrovoľníkmi aj neziskovkami.',
    section: 'discussions'
}

export const volunteeringHubLinks: ListingSeoLink[] = [
    becomeVolunteerLink,
    {
        to: { name: 'dobrovolnictvo-dobrovolnici' },
        label: 'Databáza dobrovoľníkov',
        description: 'Profily ľudí, ktorí chcú pomáhať neziskovkám a komunitám.',
        section: 'volunteering'
    },
    findVolunteersLink,
    nonProfitJobsLink,
    mentorsLink,
    volunteeringDiscussionsLink
]

export const volunteeringListingLinks: ListingSeoLink[] = [
    becomeVolunteerLink,
    findVolunteersLink,
    nonProfitJobsLink,
    volunteeringDiscussionsLink
]

export const volunteersListingLinks: ListingSeoLink[] = [
    findVolunteersLink,
    {
        to: { name: 'dobrovolnictvo-dobrovolnicke-projekty' },
        label: 'Dobrovoľnícke ponuky',
        description: 'Chceš pomáhať ty? Vyber si projekt, ktorý ťa zaujme.',
        section: 'volunteering'
    },
    {
        ...becomeVolunteerLink,
        label: 'Staň sa dobrovoľníkom',
        description: 'Zisti, ako sa zapojiť, aby ťa organizácie našli.'
    },
    {
        to: { name: 'pre-neziskovky' },
        label: 'GROWNi pre neziskovky',
        description: 'Ako môže GROWNi pomôcť tvojej neziskovke či škole.',
        section: 'organizations'
    }
]

export const mentorsListingLinks: ListingSeoLink[] = [
    {
        to: { name: 'mentoring' },
        label: 'Čo je mentoring',
        description: 'Ako mentoring funguje a v čom sa líši od koučingu.',
        section: 'mentoring'
    },
    {
        to: { name: 'mentoring-pre-mentee' },
        label: 'Pre mentees',
        description: 'S čím ti môže mentor pomôcť a ako začať.',
        section: 'mentoring'
    },
    {
        to: { name: 'mentoring-pre-mentorov' },
        label: 'Staň sa mentorom',
        description: 'Odovzdaj svoje skúsenosti ďalej a získaj nové inšpirácie.',
        section: 'mentoring'
    },
    {
        to: '/diskusie/mentoring',
        label: 'Diskusie o mentoringu',
        description: 'Mentori aj mentees tu zdieľajú skúsenosti.',
        section: 'discussions'
    }
]

/** Other GROWNi sections, for pages about mentoring. */
export const mentoringRelatedLinks: ListingSeoLink[] = [
    {
        to: { name: 'praca' },
        label: 'Práca a stáže',
        description: 'Pracovné ponuky a stáže v neziskovkách, firmách aj verejnom sektore.',
        section: 'jobs'
    },
    {
        to: { name: 'dobrovolnictvo' },
        label: 'Dobrovoľníctvo',
        description: 'Zapoj sa do projektov, ktoré menia tvoje okolie.',
        section: 'volunteering'
    },
    {
        to: { name: 'vzdelavanie' },
        label: 'Vzdelávanie',
        description: 'Workshopy, kurzy a podujatia pre tvoj rozvoj.',
        section: 'skills'
    },
    selfKnowledgeLink
]

export const educationTypeLinks: ListingSeoLink[] = [
    {
        to: { name: 'vzdelavanie', query: { type: SkillType.event } },
        label: 'Workshopy a podujatia',
        description: 'Workshopy, webináre, konferencie a ďalšie podujatia.',
        section: 'skills'
    },
    {
        to: { name: 'vzdelavanie', query: { type: SkillType.kurz } },
        label: 'Kurzy',
        description: 'Kurzy a rozvojové programy pre tvoj profesijný rast.',
        section: 'skills'
    },
    {
        to: { name: 'vzdelavanie', query: { type: SkillType.studyOpportunity } },
        label: 'Študijné a štipendijné príležitosti',
        description: 'Štipendiá a ďalšie študijné príležitosti.',
        section: 'skills'
    }
]

export const educationRelatedLinks: ListingSeoLink[] = [
    mentorsLink,
    {
        to: { name: 'kruzky' },
        label: 'Krúžky',
        description: 'Mesačné stretnutia v malej skupine pre osobný rast.',
        section: 'circles'
    },
    selfKnowledgeLink,
    {
        to: '/diskusie/vzdelavanie',
        label: 'Diskusie o vzdelávaní',
        description: 'Pýtaj sa komunity na kurzy, štúdium či rozvoj.',
        section: 'discussions'
    }
]
